import { allComorbidityOptions, initialScreeningValues, yesNoUnknownOptions } from './constants';

export function getBmiNumber(values) {
  const bmi = typeof values.bmi_at_initiation === 'number' ? values.bmi_at_initiation : Number(values.bmi_at_initiation);
  return Number.isFinite(bmi) && bmi > 0 ? bmi : null;
}

export function getInitialScreeningValues() {
  return {
    ...initialScreeningValues,
    comorbidities: [],
  };
}

export function optionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || value || 'Not answered';
}

export function screeningComorbidityLabels(values) {
  const selected = Array.isArray(values.comorbidities) ? values.comorbidities : [];
  if (!selected.length) return 'unknown';
  return selected.map((value) => optionLabel(allComorbidityOptions, value)).join(', ');
}

export function formatScreeningSummary(values) {
  const parts = [
    `Eligible Part D coverage: ${optionLabel(yesNoUnknownOptions, values.eligible_part_d_coverage)}`,
    `Received GLP-1 through Part D: ${optionLabel(yesNoUnknownOptions, values.received_glp1_through_part_d)}`,
    `T2D/OSA/MASH: ${optionLabel(yesNoUnknownOptions, values.has_part_d_diagnosis)}`,
    `Weight-management-only use: ${optionLabel(yesNoUnknownOptions, values.weight_management_only)}`,
    `BMI at initiation: ${values.bmi_at_initiation || 'unknown'}`,
    `Comorbidities: ${screeningComorbidityLabels(values)}`,
  ];
  return `Run Bridge screening with de-identified inputs. ${parts.join('; ')}.`;
}

export function screeningContextSummary(screening) {
  if (!screening) return null;
  const summary = screening.input_summary || {};
  const pieces = [
    screening.label || screening.status,
    summary.eligible_part_d_coverage ? `Part D: ${optionLabel(yesNoUnknownOptions, summary.eligible_part_d_coverage)}` : null,
    summary.received_glp1_through_part_d
      ? `Part D GLP-1: ${optionLabel(yesNoUnknownOptions, summary.received_glp1_through_part_d)}`
      : null,
    summary.bmi_at_initiation ? `BMI: ${summary.bmi_at_initiation}` : null,
  ].filter(Boolean);
  return pieces.join(' · ');
}

export function screeningCompletionItems(values) {
  const bmi = getBmiNumber(values);
  const selectedComorbidities = Array.isArray(values.comorbidities) ? values.comorbidities : [];
  const needsComorbidities = bmi !== null && bmi >= 27 && bmi < 35;
  const clinicalMissing = [
    values.weight_management_only ? null : 'weight-management use',
    bmi !== null ? null : 'BMI at initiation',
    needsComorbidities && !selectedComorbidities.length ? 'qualifying comorbidities' : null,
  ].filter(Boolean);

  return [
    {
      label: 'Part D coverage',
      done: Boolean(values.eligible_part_d_coverage),
      summary: optionLabel(yesNoUnknownOptions, values.eligible_part_d_coverage),
    },
    {
      label: 'Prior Part D GLP-1',
      done: Boolean(values.received_glp1_through_part_d),
      summary: optionLabel(yesNoUnknownOptions, values.received_glp1_through_part_d),
    },
    {
      label: 'Part D-coverable diagnosis',
      done: Boolean(values.has_part_d_diagnosis),
      summary: optionLabel(yesNoUnknownOptions, values.has_part_d_diagnosis),
    },
    {
      label: 'Clinical criteria',
      done: clinicalMissing.length === 0,
      summary: bmi !== null ? `BMI ${bmi}; ${screeningComorbidityLabels(values)}` : 'BMI not entered',
      missing: clinicalMissing,
    },
  ];
}
